import {
    Autocomplete,
    Box,
    Button,
    createFilterOptions,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@ds/coolshop';
import { useLanguageCtx } from '@modules/language';
import { useUpdateContentTypeMutation } from '@modules/product';
import { ContentType, SelectOptionValues } from '@src/__generated__/types';
import { FieldProps } from '@src/assets/types';
import { EditNodeContext } from '@src/store/hierarchy/EditNodeContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import SnackBarUtils from '../shared/SnackBarUtils';

interface IUseCreatableFieldForm {
    initialValues: {
        code: string;
        name: string;
        open: boolean;
        shouldSubmit: boolean;
    };
    fieldData: {
        ct: ContentType;
        groupCode: string;
        valueSetter: (value: string | string[]) => void;
        isMultiValue?: boolean;
        currentValue?: string | string[];
        getCurrentKeys?: () => string[];
    };
}

type CreatableOptionType = {
    code: string;
    label: string;
    /** Bare option name without the "(code)" suffix appended when showCode is active */
    name: string;
    inputValue?: string;
    /** Unique key for matching — equals code when codes are unique, code+label otherwise */
    key: string;
};

const getMandatoryFields = (ct: ContentType) => ({
    code: ct.code,
    type: ct.type,
    name: ct.name,
    mandatory: ct.mandatory,
    multiLanguage: ct.multiLanguage,
    multiValue: ct.multiValue,
    hidden: ct.hidden ?? false,
    readOnly: ct.readOnly ?? false,
    enableRange: ct.enableRange ?? false,
    showCode: ct.showCode ?? false,
    allowDuplicateOptionsCode: ct.allowDuplicateOptionsCode ?? false,
    disallowEqualCodeAndValue: ct.disallowEqualCodeAndValue ?? false,
    visibility: {
        operator: ct.visibility?.operator ?? 'or',
        visibilityRules: ct.visibility?.visibilityRules,
    },
});

const filter = createFilterOptions<CreatableOptionType>();

const INITIAL_VALUES = { code: '', name: '', open: false, shouldSubmit: false };

const useCreatableFieldForm = ({ initialValues, fieldData }: IUseCreatableFieldForm) => {
    const { defaultLanguage } = useLanguageCtx();
    const { state } = useContext(EditNodeContext);
    const [updateContentType] = useUpdateContentTypeMutation();

    const [formState, setFormState] = useState(initialValues);
    const allowDuplicates = !!fieldData.ct.allowDuplicateOptionsCode;

    const [selectOptions, setSelectOptions] = useState<CreatableOptionType[]>(
        () =>
            fieldData?.ct?.optionalValues?.map(option => {
                const name = extractDefaultName(option, defaultLanguage);
                const label = fieldData.ct.showCode
                    ? `${name} (${option.code})`
                    : name || option.code;
                const key = allowDuplicates ? `${option.code} - ${name}` : option.code;
                return { label, name, code: option.code, key };
            }) ?? [],
    );

    const handleChange = useCallback((target: string, value: string | boolean) => {
        setFormState(prev => ({ ...prev, [target]: value }));
    }, []);

    const handleSubmit = useCallback(() => {
        if (!state?.objectSchemaId) {
            return;
        }

        const { ct } = fieldData;

        // Block submission if the exact code + value pair already exists
        const isDuplicate = ct.optionalValues?.some(
            option =>
                option.code === formState.code &&
                extractDefaultName(option, defaultLanguage) === formState.name,
        );
        if (isDuplicate) {
            SnackBarUtils.error('An option with the same code and value already exists');
            setFormState(initialValues);
            return;
        }

        const mandatoryFields = getMandatoryFields(ct);

        updateContentType({
            variables: {
                aggregateId: state.objectSchemaId,
                groupCode: fieldData.groupCode,
                updateContentTypeInput: {
                    // TODO: remove the need to provide all the fields when updating
                    ...mandatoryFields,
                    optionalValues: [
                        ...(ct.optionalValues && ct.optionalValues.length > 0
                            ? ct.optionalValues
                            : []),
                        {
                            code: formState.code,
                            value: [
                                {
                                    languageIsoCode: defaultLanguage,
                                    value: formState.name,
                                },
                            ],
                        },
                    ],
                },
            },
        })
            .then(() => {
                const label = fieldData.ct.showCode
                    ? `${formState.name} (${formState.code})`
                    : formState.name || formState.code;
                const key = allowDuplicates
                    ? `${formState.code} - ${formState.name}`
                    : formState.code;
                const newOption = { label, name: formState.name, code: formState.code, key };
                setSelectOptions(prev => [...prev, newOption]);
                setFormState(initialValues);

                if (fieldData.isMultiValue) {
                    const currentKeys = fieldData.getCurrentKeys?.() ?? [];
                    fieldData.valueSetter([...currentKeys, key]);
                } else {
                    fieldData.valueSetter(key);
                }
                SnackBarUtils.success('New select option added');
            })
            .catch(() => {
                SnackBarUtils.error('Failed to add new select option');
            });
    }, [formState]);

    useEffect(() => {
        if (formState.shouldSubmit) {
            handleSubmit();
        }
    }, [formState.shouldSubmit]);

    return { formState, selectOptions, handleChange };
};

function extractDefaultName(option: SelectOptionValues, defaultLanguage: string) {
    if (!Array.isArray(option.value)) {
        return '';
    }

    const foundValue = option.value.find(v => v.languageIsoCode === defaultLanguage);
    return foundValue?.value ?? option.value[0].value ?? '';
}

function CreatableFieldElement({
    ct,
    id,
    groupCode,
    label,
    control,
    name,
    disabled,
    readOnly,
    isMultiValue,
}: FieldProps['creatable']) {
    const intl = useIntl();
    const allowDuplicates = !!ct.allowDuplicateOptionsCode;

    const { field } = useController({
        control,
        name,
    });

    // The `code` field on NodeContentValue sits as a sibling of `value` in the form structure
    // e.g. name = "group.ctCode.value.0.value.0" → codePath = "group.ctCode.value.0.code"
    const codePath = name.replace(/\.value(\.\d+)?$/, '.code');
    const { setValue, getValues } = useFormContext();

    // Track the currently selected key(s) in local state so the Autocomplete re-renders
    // immediately when the user picks a different option with the same base code
    const [selectedKey, setSelectedKey] = useState<string | string[] | null>(() => {
        const storedCode: string | undefined = getValues(codePath);
        if (!storedCode) return null;
        if (isMultiValue) {
            try {
                return JSON.parse(storedCode) as string[];
            } catch {
                return null;
            }
        }
        return storedCode;
    });

    const setFieldValue = useCallback(
        (val: string | string[]) => {
            if (allowDuplicates && Array.isArray(val)) {
                setValue(codePath, JSON.stringify(val), { shouldDirty: true });
                setSelectedKey(val);
                field.onChange(val.map(v => v.split(' - ')[0]));
            } else if (allowDuplicates && !Array.isArray(val)) {
                setValue(codePath, val || undefined, { shouldDirty: true });
                setSelectedKey(val || null);
                const optionCode = val ? val.split(' - ')[0] : '';
                field.onChange(optionCode);
            } else {
                field.onChange(val);
            }
        },
        [allowDuplicates, codePath, setValue, field],
    );

    const getCurrentKeys = useCallback(() => {
        if (allowDuplicates && Array.isArray(selectedKey)) {
            return selectedKey;
        }
        return Array.isArray(field.value) ? field.value : [];
    }, [allowDuplicates, selectedKey, field.value]);

    const { formState, selectOptions, handleChange } = useCreatableFieldForm({
        initialValues: INITIAL_VALUES,
        fieldData: {
            ct,
            groupCode,
            valueSetter: setFieldValue,
            isMultiValue,
            currentValue: field.value,
            getCurrentKeys,
        },
    });

    // Resolve the displayed option: use selectedKey (composite key) if available, fall back to value
    const resolvedValue = useMemo(() => {
        if (isMultiValue) {
            if (allowDuplicates && Array.isArray(selectedKey)) {
                return selectedKey
                    .map(k => selectOptions.find(opt => opt.key === k))
                    .filter(Boolean) as CreatableOptionType[];
            }
            return selectOptions.filter(
                opt =>
                    Array.isArray(field.value) && field.value.some((v: string) => v === opt.code),
            );
        }
        if (allowDuplicates && typeof selectedKey === 'string') {
            const match = selectOptions.find(opt => opt.key === selectedKey);
            if (match) return match;
        }
        return selectOptions.find(opt => opt.code === field.value) ?? null;
    }, [isMultiValue, field.value, selectOptions, allowDuplicates, selectedKey]);

    // When the schema shows the option code in the label as "name (code)", parentheses
    // in the option name would clash with that formatting — flag them as an error
    const nameHasParentheses = !!ct.showCode && /[()]/.test(formState.name);
    const parenthesesErrorMessage = intl.formatMessage({
        id: 'creatable-field.name-parentheses',
        defaultMessage: 'The characters ( and ) are not allowed in the name',
    });
    const codeHasParentheses = !!ct.showCode && /[()]/.test(formState.code);
    const codeParenthesesErrorMessage = intl.formatMessage({
        id: 'creatable-field.code-parentheses',
        defaultMessage: 'The characters ( and ) are not allowed in the code',
    });

    const handleClose = () => {
        handleChange('open', false);
    };

    // Surface the "code must differ from value" constraint inline in the dialog so the user
    // is warned before submitting instead of after
    const codeEqualsValue =
        !!ct.disallowEqualCodeAndValue &&
        formState.code.trim() !== '' &&
        formState.code.trim() === formState.name.trim();
    const codeEqualsValueMessage = intl.formatMessage({
        id: 'creatable-field.code-equals-value',
        defaultMessage: 'Code and value must be different',
    });

    return (
        <>
            <Autocomplete
                id={id}
                multiple={isMultiValue}
                disabled={disabled || readOnly || field.disabled}
                minWidth={222}
                label={label}
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}
                value={resolvedValue}
                onChange={(_, value) => {
                    if (isMultiValue && Array.isArray(value)) {
                        const lastItem = value[value.length - 1];
                        if (
                            typeof lastItem === 'object' &&
                            lastItem !== null &&
                            'inputValue' in lastItem &&
                            lastItem.inputValue
                        ) {
                            handleChange('code', '');
                            handleChange('name', lastItem.inputValue);
                            handleChange('open', true);
                            return;
                        }
                        const keys = value.map((v: CreatableOptionType | string) =>
                            typeof v === 'string' ? v : v.key,
                        );
                        setFieldValue(keys);
                        return;
                    }

                    if (typeof value === 'string') return;

                    if (!value) {
                        setFieldValue('');
                        return;
                    }

                    if (!Array.isArray(value) && value.inputValue) {
                        handleChange('code', '');
                        handleChange('name', value.inputValue);
                        handleChange('open', true);
                        return;
                    }

                    if (!Array.isArray(value)) {
                        setFieldValue(value.key);
                    }
                }}
                filterOptions={(options, params) => {
                    const filtered = filter(options as CreatableOptionType[], params);

                    const { inputValue } = params;
                    // Suggest the creation of a new value only when the typed text does not
                    // already exist among the options — matching (trimmed, case-insensitive)
                    // an option code, its bare name or its full label (which includes the
                    // "(code)" suffix when showCode is active) hides the "create" entry
                    const normalizedInput = inputValue.trim().toLowerCase();
                    const isExisting = options.some(
                        option =>
                            typeof option !== 'string' &&
                            (normalizedInput === option.code.trim().toLowerCase() ||
                                normalizedInput === option.name.trim().toLowerCase() ||
                                normalizedInput === option.label.trim().toLowerCase()),
                    );
                    if (inputValue !== '' && !isExisting) {
                        filtered.push({
                            label: intl.formatMessage(
                                {
                                    id: 'creatable-field.create-new',
                                    defaultMessage: 'Create new: "{value}"',
                                },
                                { value: inputValue },
                            ),
                            inputValue,
                            name: inputValue,
                            code: inputValue,
                            key: inputValue,
                        });
                    }

                    return filtered;
                }}
                getOptionLabel={(option: CreatableOptionType | string) => {
                    if (typeof option === 'string') return option;
                    // Keep the plain typed text in the input box for the "create new" entry
                    if (option.inputValue) return option.inputValue;
                    return option.label ?? option.code ?? '';
                }}
                isOptionEqualToValue={(option, val) =>
                    typeof option !== 'string' && typeof val !== 'string'
                        ? option.key === val.key
                        : option === val
                }
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                options={selectOptions}
                renderOption={(renderProps, option) => {
                    if (typeof option === 'string') {
                        return (
                            <li {...renderProps} key={option}>
                                {option}
                            </li>
                        );
                    }
                    // Render the synthetic "create new" entry distinctly from existing options
                    if (option.inputValue) {
                        return (
                            <li {...renderProps} key={option.key}>
                                <Typography variant="body1" color="primary">
                                    {`+ ${option.label}`}
                                </Typography>
                            </li>
                        );
                    }
                    return (
                        <li {...renderProps} key={option.key}>
                            {option.label}
                        </li>
                    );
                }}
                freeSolo
            />
            <Dialog
                open={formState.open}
                onClose={handleClose}
                fullWidth
                maxWidth="xs"
                disablePaddings
            >
                <DialogTitle>
                    <Typography variant="h5">
                        {intl.formatMessage({
                            id: 'new-option.title',
                            defaultMessage: 'New Select Option',
                        })}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        {intl.formatMessage(
                            {
                                id: 'new-option.description',
                                defaultMessage: 'Add a new select option for the field {label}',
                            },
                            {
                                label,
                            },
                        )}
                    </Typography>
                    <Stack direction="row" gap={1} mt={2}>
                        <TextField
                            autoFocus
                            fullWidth
                            id="code"
                            type="text"
                            label="code"
                            value={formState.code}
                            error={codeEqualsValue || codeHasParentheses}
                            onChange={e => {
                                handleChange('code', e.target.value);
                            }}
                        />
                        <TextField
                            fullWidth
                            id="name"
                            type="text"
                            label="name"
                            value={formState.name}
                            error={codeEqualsValue || nameHasParentheses}
                            onChange={e => {
                                handleChange('name', e.target.value);
                            }}
                        />
                    </Stack>
                    {/* Fixed-height area so the dialog does not grow when the validation
                        message toggles on and off */}
                    <Box minHeight={24} mt={0.5} display="flex" flexDirection="column">
                        {codeEqualsValue && (
                            <Typography variant="caption" color="error">
                                {codeEqualsValueMessage}
                            </Typography>
                        )}
                        {nameHasParentheses && (
                            <Typography variant="caption" color="error">
                                {parenthesesErrorMessage}
                            </Typography>
                        )}
                        {codeHasParentheses && (
                            <Typography variant="caption" color="error">
                                {codeParenthesesErrorMessage}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>
                        {intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' })}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={codeEqualsValue || nameHasParentheses || codeHasParentheses}
                        onClick={() => {
                            handleChange('shouldSubmit', true);
                            handleChange('open', false);
                        }}
                    >
                        {intl.formatMessage({ id: 'add', defaultMessage: 'Add' })}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default CreatableFieldElement;
